import { cardsGroupInRevisionStatus, datesColors, monthsColors, weeksColors } from "../constants/constants"
import { hasElementsMatch } from "./finder"
import { _ } from "./translator"
import { dateIsOlderThanToday, daysDifferenceBetweenDates, getDatesBetweenDates, getRandomElement } from "./utils"

export const getAttr = (element, key, nullMessage='') => {
    if (element) {
        if (element.attributes){
            if (element.attributes[key]) {
                return element.attributes[key]            
            }
        }
    }
    return nullMessage
}

export const get = (element, key, nullMessage='') => {
    if (element) {
        if (element[key]) {
            return element[key]
        }
    }
    return nullMessage
}

export function getCardsGroupIdsToLoad(cardsGroupsData, cardsGroupIds) {
    let cardsGroupIdsToLoad = [];
    for (const cardsGroupId of cardsGroupIds) {
        if (!cardsGroupsData[cardsGroupId]) {
            cardsGroupIdsToLoad.push(cardsGroupId);
        }
    }
    return cardsGroupIdsToLoad;
}

export function getAvailableValuesFromAttributes(elementsData, attributeId, elementIds=null) {
    let values = [];
    if (elementIds) {
        for (const elementId of elementIds) {
            let value = elementsData[elementId]?.attributes[attributeId];
            if (value && !values.includes(value)) {
                values.push(value);
            }
        }
    } else {
        for (const elementData of Object.values(elementsData)) {
            let value = elementData.attributes[attributeId];
            if (value && !values.includes(value)) {
                values.push(value);
            }
        }
    }
    return values;
}

export function getFiguregroupsMap(figuresData) {
    let figuregroupsMap = {};
    for (const figureId in figuresData) {
        let figureData = figuresData[figureId];
        for (const figuregroupId in figureData.figuregroups) {
            let figureGroupData = figureData.figuregroups[figuregroupId];
            if (figuregroupsMap.hasOwnProperty(figureGroupData.id)) {
                figuregroupsMap[figureGroupData.id].push(figureData.id);
            } else {
                figuregroupsMap[figureGroupData.id] = [figureData.id];
            }    
        }  
    }
    return figuregroupsMap;
}

export function getUsername(userId, usersData, includePosition=true) {
    const userData = usersData[userId];
    if (!userData) {
        return 'Usuario Fuera del Proyecto';
    }
    const firstName = userData.attributes.first_name;
    const lastName = userData.attributes.last_name;
    const position = userData.attributes.position;
    const username = window.is_demo ? getRandomElement(window.demo_user_names) : `${firstName} ${lastName}`;
    if (includePosition) {
        return `${username} - ${position}`;
    }
    return username;
}

export function getDelayedCardsGroupIds(cardsGroupsData) {
    const delayedCardsGroupIds = [];
    for (const cardsGroupData of Object.values(cardsGroupsData)) {
        const status = cardsGroupData.attributes.status;
        const finishDate = cardsGroupData.attributes.finish_date;
        const cardsGroupIsInRevision = cardsGroupInRevisionStatus.includes(status);
        if (!cardsGroupIsInRevision || !finishDate) continue;
        const isDelayed = dateIsOlderThanToday(finishDate);
        if (isDelayed) {
            delayedCardsGroupIds.push(cardsGroupData.id);
        }
    }
    return delayedCardsGroupIds;
}

export function getAnswersInformation(answersData) {
    const questionIdsWithAnswers = [];
    const questionoptionIdsWithAnswers = [];
    const answersDict = {};
    for (const answerData of Object.values(answersData)) {
        const questionId = answerData.attributes.question_id;
        const questionoptionId = answerData.attributes.questionoption_id;
        if (!questionIdsWithAnswers.includes(questionId)) {
            questionIdsWithAnswers.push(questionId);
        }
        if (questionoptionId && !questionoptionIdsWithAnswers.includes(questionoptionId)) {
            questionoptionIdsWithAnswers.push(questionoptionId);
        }
        if (questionoptionId) {
            const answerKey = `${questionId}_${questionoptionId}`;
            answersDict[answerKey] = answerData;
        } else {
            answersDict[questionId] = answerData;
        }
    }
    return {
        questionIdsWithAnswers,
        questionoptionIdsWithAnswers,
        answersDict
    }
}

export function getAvailableCardCategoryIds(cardCategoriesData, stageIds, levelIds, matches) {
    const cardCategoryIds = [];
    for (const categoryData of Object.values(cardCategoriesData)) {
        if ((stageIds.includes(categoryData.attributes.stage_id) || stageIds.length === 0)
            && hasElementsMatch(levelIds, matches, categoryData.id)) {
            cardCategoryIds.push(categoryData.id);
        }
    }
    return cardCategoryIds;
}

export function getBimElement(bimDescription) {
    const descriptionSplitted = bimDescription?.split(':');
    if (descriptionSplitted && descriptionSplitted.length > 1) {
        descriptionSplitted.splice(descriptionSplitted.length - 1, 1);
        const bimElement = descriptionSplitted.join(':');
        return bimElement;
    };
    return '';
}

export function getStartDateLegendInfo(days) {
    let legend = {}
    const weekDays = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    const englishWeekDays = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const daysDifference = daysDifferenceBetweenDates(days.first, days.last);
    if (daysDifference <= 6) {
        const datesBetweenDates = getDatesBetweenDates(days.first, days.last);
        datesBetweenDates.forEach(date => {
            let day = date.getDay()
            legend[englishWeekDays[day]] = {
                color: datesColors[englishWeekDays[day]],
                description: _(`${weekDays[day]} ${date.toISOString().split('T')[0]}`),
            }
        })
    }
    if (daysDifference > 6 && daysDifference <= 56) {
        const weeks = ['Semana 1', 'Semana 2', 'Semana 3', 'Semana 4', 'Semana 5', 'Semana 6', 'Semana 7', 'Semana 8'];
        const totalWeeks = getTotalWeekFromDates(days.first, days.last);
        const finalTotalWeeks = weeks.slice(0, totalWeeks)
        finalTotalWeeks.forEach(week => {
            legend[week] = {
                color: weeksColors[week],
                description: _(`${week}`),
            }
        })
    }
    if (daysDifference > 56) {
        const months = ['Mes 1', 'Mes 2', 'Mes 3', 'Mes 4', 'Mes 5', 'Mes 6', 'Mes 7', 'Mes 8 +'];
        const totalMonths = getTotalWMonthsFromDates(days.first, days.last);
        if (totalMonths > 8) {
            months.forEach(month => {
                legend[month] = {
                    color: monthsColors[month],
                    description: _(`${month}`),
                }
            })
        } else {
            const finalTotalMonths = months.slice(0, totalMonths)
            finalTotalMonths.forEach(month => {
                legend[month] = {
                    color: monthsColors[month],
                    description: _(`${month}`),
                }
            })
        }
    }
    return legend
}

export function getWeekNumberFromDates(initDate, endDate, date) {
    const init = new Date(initDate);
    const actualDate = new Date(date);
    
    if (actualDate < init || actualDate > new Date(endDate)) {
        return -1;
    }
    
    const oneDay = 1000 * 60 * 60 * 24;
    const daysDifference = Math.floor((actualDate - init) / oneDay);
    
    return Math.floor(daysDifference / 7) + 1;
}


export function getTotalWeekFromDates(initDate, endDate) {
    const init = new Date(initDate);
    const end = new Date(endDate);
    
    const oneDay = 1000 * 60 * 60 * 24;
    const totalDaysDifference = Math.floor((end - init) / oneDay);
    
    return Math.ceil((totalDaysDifference + 1) / 7);
}

export function getMonthNumberFromDates(initDate, endDate, date) {
    const init = new Date(initDate);
    const end = new Date(endDate);
    const actualDate = new Date(date);
    
    if (actualDate < init || actualDate > end) {
        return -1;
    }

    const monthsDifference = (actualDate.getFullYear() - init.getFullYear()) * 12 + (actualDate.getMonth() - init.getMonth());
    return monthsDifference + 1;
}

export function getTotalWMonthsFromDates(initDate, endDate) {
    const init = new Date(initDate);
    const end = new Date(endDate);
    
    const totalMonths = (end.getFullYear() - init.getFullYear()) * 12 + (end.getMonth() - init.getMonth());
    return totalMonths + 1;
}
